import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import * as Flags from "country-flag-icons/react/3x2";
import { orderedCountries, type Country } from "~/helper/form/dialCodes";
import {
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
} from "~/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { cn } from "~/lib/utils";

type FlagComponent = React.FC<{ title?: string; className?: string }>;

const CountryFlag = ({ country, className }: { country: Country; className?: string }) => {
    const Flag = (Flags as Record<string, FlagComponent>)[country.code];
    if (Flag) {
        return <Flag title={country.name} className={className} />;
    }
    return <span className={className}>{country.emoji}</span>;
};

type Props = {
    /** Selected country ISO code (e.g. "DE"). */
    value: string;
    onSelect: (country: Country) => void;
    triggerClassName?: string;
    searchPlaceholder?: string;
    emptyText?: string;
};

const CountryCodeCombobox = ({
    value,
    onSelect,
    triggerClassName,
    searchPlaceholder = "Search country",
    emptyText = "No country found",
}: Props) => {
    const [open, setOpen] = useState(false);

    const selected = useMemo(
        () => orderedCountries.find((c) => c.code === value) ?? orderedCountries[0],
        [value],
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        "flex h-10 w-28 items-center justify-between px-3 py-2 bg-transparent text-burgundy outline-none focus:ring-0 focus-visible:ring-0",
                        triggerClassName,
                    )}
                >
                    <span className="flex items-center gap-2">
                        <CountryFlag country={selected} className="w-4 shrink-0" />
                        <span className="text-sm font-light">{selected.dial_code}</span>
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
                <Command>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        {orderedCountries.map((country) => (
                            <CommandItem
                                key={country.code}
                                value={country.code}
                                keywords={[country.name, country.dial_code]}
                                onSelect={() => {
                                    onSelect(country);
                                    setOpen(false);
                                }}
                            >
                                <CountryFlag country={country} className="w-5 shrink-0" />
                                <span className="flex-1 truncate">{country.name}</span>
                                <span className="text-muted-foreground text-sm">
                                    {country.dial_code}
                                </span>
                            </CommandItem>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default CountryCodeCombobox;
